import { useCallback, useEffect } from "react";
import { findIndex } from "lodash";

import { useAssetsList } from "@/contexts/AssetsListContext";
import { resolveEntryAsset } from "@/components/AssetsList/resolveEntryAsset";
import { UNSAVED_ASSETS_LIST_LABEL } from "@/components/AssetsList/constants";
import AssetsListAssetPreviewLayout from "./AssetsListAssetPreviewLayout";
import { IMAGE_ENTRY_IN_TIME, IMAGE_ENTRY_OUT_TIME } from "./constants";

/**
 * @input {tagName: 'INPUT'}
 * @output true
 */
function isTypingTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;

  return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName);
}

/**
 * Smart container for the assets-list overlay preview. Owns every decision the
 * panel makes — which entry is showing, where the chevrons go next, what a
 * delete means — and hands the presentational tree nothing but values and
 * callbacks.
 *
 * Renders null unless an entry is open, so the parent can mount it
 * unconditionally and let the context decide.
 *
 * Deliberately *not* a modal: the panel is absolutely positioned against the
 * editor area, which leaves the list strip below it live and anchored.
 */
export default function AssetsListAssetPreviewContainer({ resolveAssetById }) {
  const {
    workingList,
    previewEntryKey,
    openEntryPreview,
    closeEntryPreview,
    removeEntry,
    updateEntryTrim,
    updateEntrySourceMedia,
  } = useAssetsList();

  const { entries } = workingList;
  const previewedIndex = findIndex(
    entries,
    (entry) => entry.entryKey === previewEntryKey,
  );
  const previewedEntry = previewedIndex === -1 ? null : entries[previewedIndex];

  // An entry can vanish underneath us — a hard delete of the asset, or another
  // list being loaded. Closing here rather than rendering a blank panel.
  useEffect(() => {
    if (!previewEntryKey) return;
    if (previewedEntry) return;

    closeEntryPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewEntryKey, previewedEntry]);

  const goToEntryAt = (index) => {
    const entryAtIndex = entries[index];
    if (!entryAtIndex) return;

    openEntryPreview({ entryKey: entryAtIndex.entryKey });
  };

  useEffect(() => {
    if (!previewedEntry) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeEntryPreview();
        return;
      }

      if (event.defaultPrevented) {
        return;
      }

      if (isTypingTarget(event.target)) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToEntryAt(previewedIndex - 1);
        return;
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToEntryAt(previewedIndex + 1);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewedEntry, previewedIndex, entries]);

  /**
   * An entry dragged straight into the strip arrives with nothing measured —
   * there was no player behind the drop to measure it. Opening it here is the
   * first time a frame rate and frame size can be read, and the edit decision
   * list export needs both, so record them on the entry as they land.
   *
   * Stable across renders on purpose: the player reports on every measurement
   * change, and a fresh callback identity each render would have it reporting
   * in a loop.
   */
  const handleMediaInfoChange = useCallback(
    (mediaInfo) => {
      if (!previewEntryKey) return;

      updateEntrySourceMedia({
        entryKey: previewEntryKey,
        sourceMedia: mediaInfo,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewEntryKey],
  );

  if (!previewedEntry) return null;

  const previewedAsset = resolveEntryAsset({
    assetId: previewedEntry.asset_id,
    resolveAssetById,
    assetsById: workingList.assetsById,
  });

  const hasPrevious = previewedIndex > 0;
  const hasNext = previewedIndex < entries.length - 1;

  const handlePrevious = () => goToEntryAt(previewedIndex - 1);

  const handleNext = () => goToEntryAt(previewedIndex + 1);

  /**
   * Trims are stored on the entry, not the asset, and land in the persisted
   * draft through the provider's own write effect — so the user never saves to
   * keep a handle drag. Pushing them to the server is still Save's job.
   */
  const handleTrimChange = ({ inTime, outTime }) => {
    updateEntryTrim({
      entryKey: previewedEntry.entryKey,
      inTime,
      outTime,
    });
  };

  /**
   * Removes the list entry only. The asset itself is untouched — no archive,
   * no delete — which is the whole reason this panel exists rather than
   * reusing the grid's preview.
   *
   * The successor slides into the vacated slot, so we stay on that index and
   * only fall back to the entry before it when the removed one was last.
   */
  const handleRemoveFromList = () => {
    const remainingCount = entries.length - 1;
    removeEntry({ entryKey: previewedEntry.entryKey });

    if (remainingCount === 0) {
      closeEntryPreview();
      return;
    }

    const successorIndex = Math.min(previewedIndex, remainingCount - 1);
    const remainingEntries = entries.filter(
      (entry) => entry.entryKey !== previewedEntry.entryKey,
    );
    openEntryPreview({ entryKey: remainingEntries[successorIndex].entryKey });
  };

  // An image has no timeline to read a selection off, so its entry carries a
  // fixed duration instead of whatever the handles would have said.
  const isImageEntry = previewedAsset?.mediaType === "image";
  const initialTrimInTime = isImageEntry
    ? IMAGE_ENTRY_IN_TIME
    : previewedEntry.in_time;
  const initialTrimOutTime = isImageEntry
    ? IMAGE_ENTRY_OUT_TIME
    : previewedEntry.out_time;

  return (
    <AssetsListAssetPreviewLayout
      asset={previewedAsset}
      assetName={previewedAsset?.name || "Asset unavailable"}
      // The delete button names the list it removes from, so it can never be
      // mistaken for the grid preview's Archive.
      listName={workingList.name || UNSAVED_ASSETS_LIST_LABEL}
      // Keyed on the entry, not the asset: the same asset can sit in the list
      // twice with different trims, and stepping between them has to remount.
      mediaKey={previewedEntry.entryKey}
      initialTrimInTime={initialTrimInTime}
      initialTrimOutTime={initialTrimOutTime}
      hasPrevious={hasPrevious}
      hasNext={hasNext}
      onPrevious={handlePrevious}
      onNext={handleNext}
      onTrimChange={handleTrimChange}
      onMediaInfoChange={handleMediaInfoChange}
      onRemoveFromList={handleRemoveFromList}
      onClose={closeEntryPreview}
    />
  );
}
